import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';
import FetchModule from '../../Model/Share/Network/FetchModule';

import useAiChatServer from '../Model/useAiChatServer';
import AiChattingView from '../../AiManager/Component/AiChattingView';
import TokenUsageGauge from '../../AiManager/Component/TokenUsageGauge';
import NotRegistrationCardModal from '../../Model/Share/Normal/NotRegistrationCardModal';
import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';
import { ReactComponent as Ai2Svg } from '../../image/Ai2.svg';
import TokenLightIcon from '../../image/Token-light.png';
import TokenDarkIcon from '../../image/Token-dark.png';
import NewChatLight from '../../image/NewChat-light.png';
import NewChatDark from '../../image/NewChat-dark.png';
import DeleteIcon from '../../image/Delete(stroke).svg';
import DeleteHoverIcon from '../../image/Delete(stroke)_hover.svg';

// AiManager/Component/AiView.js와 동일 UI — 훅만 useAiChatServer(씬 클라이언트)로 교체 (C′ §8) //
export default function AiView() {
  const history = useHistory();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const {
    // 상태
    chatMessages,
    chatInput,
    isResponding,
    usagePercent,
    sessions,
    sessionId,
    chatMessagesContainerRef,
    messageRatings,

    // 액션
    setChatInput,
    handleSend,
    handleStop,
    handleNewChat,
    loadConversations,
    deleteSession,
    handleRateMessage,
    isMessageRatable,
    handleSmsSend,
    handleIndividualSmsSend,
    handleIndividualSmsGenerate,
  } = useAiChatServer();

  const [isRegistrationCard, setIsRegistrationCard] = useState(false);
  const [isViewRegistrationCard, setIsViewRegistrationCard] = useState(false);

  // 문자 결제카드 등록 여부 조회 //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('message/status', 'GET');

        if (responseData.status === 200) setIsRegistrationCard(responseData.data.isRegistrationCard);
      }
    )();
  }, []);

  // 문자 일괄 발송 — 결제카드 미등록 시 등록 모달 (MessageSendPage 동일 게이트) //
  function onClickSmsSend(messageId, templateId, templateText, recipients) {
    if (!isRegistrationCard) {
      setIsViewRegistrationCard(true);
      return;
    }

    handleSmsSend(messageId, templateId, templateText, recipients);
  }

  // 문자 개별 발송 — 결제카드 미등록 시 등록 모달 //
  function onClickIndividualSmsSend(messageId, memberId, text) {
    if (!isRegistrationCard) {
      setIsViewRegistrationCard(true);
      return;
    }

    handleIndividualSmsSend(messageId, memberId, text);
  }

  // 결제카드 등록 위젯 호출 (Payple) //
  function onClickRegistrationCard() {
    const obj = {};

    obj.PCD_PAY_TYPE = 'card';
    obj.PCD_PAY_WORK = 'AUTH';
    obj.PCD_CARD_VER = '01';
    obj.PCD_PAYER_AUTHTYPE = 'sms';

    obj.callbackFunction = getResult;
    obj.clientKey = '0F44C92BFB07F1BE60C5235A7254316D';

    window.PaypleCpayAuthCheck(obj);
  }

  // 결제카드 등록 콜백 //
  function getResult(res) {
    if (res.PCD_PAY_RST === 'success' && (res.PCD_PAY_CODE === '0000' || res.PCD_PAY_CODE === 'CDAU1000')) {
      const requestData = {
        billingKey: res.PCD_PAYER_ID,
        cardName: res.PCD_PAY_CARDNAME,
        cardNum: res.PCD_PAY_CARDNUM
      };

      const fetchModule = new FetchModule();
      (
        async () => {
          const responseData = await fetchModule.postDataVersion2('billing/registry', 'POST', requestData);

          if (responseData.status === 200) {
            alert(t('messageSend.alertCardRegistered'));
            setIsRegistrationCard(true);
            setIsViewRegistrationCard(false);
          }
          else if (responseData.status === 201) {
            alert(t('messageSend.alertAlreadyRegistered'));
            setIsRegistrationCard(true);
            setIsViewRegistrationCard(false);
          }
        }
      )();
    }
    else {
      if (res.PCD_PAY_CODE === undefined) alert(res.PCD_PAY_MSG);
      else alert('[' + res.PCD_PAY_CODE + '] ' + res.PCD_PAY_MSG);
    }
  }

  // 뒤로가기 //
  function onClickGoBack() {
    history.goBack();
  }

  return (
    <TopLevelWrapper backgroundColor={theme.background}>
      <ComponentsWrapper>
        <NavigationBarWrapper>
          <NavigationBarLeftWrapper>
            <NavigationView $bg={theme.primary}>
              <GoBackButton onClick={onClickGoBack}>
                <GoBackIcon />
              </GoBackButton>
              <AiComponent>
                <AiIcon />
                <Text fontFamily='NotoSansKR-Medium' fontSize={18} color={theme.background} cursor='default'>AI</Text>
              </AiComponent>
            </NavigationView>
            <ExplainWrapper>
                <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{t('aiManager.title')}</Text>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{t('aiManager.subtitle')}</Text>
            </ExplainWrapper>
          </NavigationBarLeftWrapper>
          <TokenBox $borderColor={theme.primary}>
            <Image width={22} height={18} src={isDark ? TokenDarkIcon : TokenLightIcon} />
            <TokenUsageGauge usagePercent={usagePercent} normalColor={theme.primary} trackColor={theme.primaryAlpha20} />
          </TokenBox>
        </NavigationBarWrapper>
        <InfoWrapper>
          <ChattingSideBarWrapper>
            <NewChattingButton onClick={handleNewChat}>
              <Image width={20} height={19} src={isDark ? NewChatDark : NewChatLight} />
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={isDark ? theme.primary : theme.text} cursor='pointer'>{t('aiManager.newChat')}</Text>
            </NewChattingButton>
            <NewChattingListTextWrapper>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={isDark ? theme.primary : theme.text} cursor='default'>{t('aiManager.chatList')}</Text>
            </NewChattingListTextWrapper>
            <SessionList $thumbColor={theme.scrollBar}>
              {sessions.map(session => (
                <SessionItem key={session.sessionId} $isActive={session.sessionId === sessionId} $activeColor={theme.primaryAlpha20} onClick={() => loadConversations(session.sessionId)}>
                  <SessionTitle color={theme.text}>{session.sessionTitle}</SessionTitle>
                  <DeleteButton onClick={(e) => { e.stopPropagation(); deleteSession(session.sessionId); }}>
                    <DeleteImg src={DeleteIcon} />
                    <DeleteHoverImg src={DeleteHoverIcon} />
                  </DeleteButton>
                </SessionItem>
              ))}
            </SessionList>
          </ChattingSideBarWrapper>
          <AiChattingView
            chatMessages={chatMessages}
            chatInput={chatInput}
            setChatInput={setChatInput}
            handleSend={handleSend}
            handleStop={handleStop}
            isResponding={isResponding}
            chatMessagesContainerRef={chatMessagesContainerRef}
            onSmsSend={onClickSmsSend}
            onIndividualSmsSend={onClickIndividualSmsSend}
            onIndividualSmsGenerate={handleIndividualSmsGenerate}
            messageRatings={messageRatings}
            onRateMessage={handleRateMessage}
            isMessageRatable={isMessageRatable}
          />
        </InfoWrapper>
      </ComponentsWrapper>
      {
        isViewRegistrationCard &&
        <NotRegistrationCardModal division='Notice' setIsViewModal={setIsViewRegistrationCard} onClickRegistCard={onClickRegistrationCard} />
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  
  width: 100%;

  justify-content: center;
  align-items: flex-start;

  background-color: ${props => props.backgroundColor};
`;

const ComponentsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;

  width: 100%;

  min-height: 590px;
  height: calc(100vh - 102px);
`;

const NavigationBarWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
  min-width: 650px;
  height: 60px;

  gap: 10px;
`;

const NavigationBarLeftWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 8px;
`;

const NavigationView = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 0 16px 0 5px;

  box-sizing: border-box;
  width: 101px;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.$bg};
`;

const GoBackButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;

  cursor: pointer;
`;

const AiComponent = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  gap: 10px;
`;

const ExplainWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: baseline;

  padding: 0 0 0 20px;

  gap: 10px;
`;

const TokenBox = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  padding: 0 15px 0 15px;

  height: 40px;

  gap: 10px;

  border-radius: 10px;
  border: 1px solid ${props => props.$borderColor};
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: flex-start;

  padding: 10px 0 0 0;

  width: 100%;
  flex: 1;
  min-height: 0;

  gap: 15px;
`;

const ChattingSideBarWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 180px;
  height: 100%;
  min-height: 0;

  box-sizing: border-box;
`;

const NewChattingButton = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  padding: 0 10px 0 10px;

  width: 100%;
  height: 40px;

  gap: 10px;

  &:hover {
    cursor: pointer;
  }
`;

const NewChattingListTextWrapper = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  padding: 0 10px 0 10px;

  width: 100%;
  height: 40px;
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
  flex: 1;

  overflow-y: auto;

  padding-right: 5px;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.$thumbColor};
    border-radius: 2px;
  }
`;

const SessionItem = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 8px 10px;
  box-sizing: border-box;
  width: 173px;

  border-radius: 6px;

  cursor: pointer;

  background-color: ${props => props.$isActive ? props.$activeColor : 'transparent'};

  &:hover {
    background-color: ${props => props.$activeColor};
  }

  &:hover > div {
    opacity: 1;
  }
`;

const DeleteButton = styled.div`
  position: relative;

  display: flex;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  flex-shrink: 0;

  opacity: 0;

  transition: opacity 0.15s ease-in-out;

  &:hover > img:first-child {
    opacity: 0;
  }

  &:hover > img:last-child {
    opacity: 1;
  }
`;

const DeleteImg = styled.img`
  position: absolute;
  width: 16px;
  height: 16px;

  object-fit: contain;

  opacity: 1;

  transition: opacity 0.15s ease-in-out;
`;

const DeleteHoverImg = styled.img`
  position: absolute;
  width: 16px;
  height: 16px;

  object-fit: contain;

  opacity: 0;

  transition: opacity 0.15s ease-in-out;
`;

const SessionTitle = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0;
  min-width: 0;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Image = styled.img`
  width: ${props => props.width}px;
  height: ${props => props.height}px;

  object-fit: contain;
`;

const GoBackIcon = styled(LeftArrowSvg)`
  flex-shrink: 0;
  width: 9px;

  color: ${props => props.theme.subBackground};
`;

const AiIcon = styled(Ai2Svg)`
  flex-shrink: 0;
  width: 23px;
  height: auto;

  & path { fill: ${props => props.theme.subBackground}; }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;

  cursor: ${props => props.cursor};
`;
